/**
 * Encryption utilities
 * 
 * This module provides encryption/decryption functions and access to the encryption key
 * stored in environment variables. The key is used to encrypt sensitive data before sending to the API.
 */

import CryptoJS from 'crypto-js';
import { getEncryptKey } from './obfuscate';

/**
 * Get the encryption key from environment variables
 * @returns The encryption key string
 * @throws Error if the key is not configured
 */
export const getEncryptionKey = (): string => {
  const key = getEncryptKey();
  
  if (!key) {
    console.error('VITE_ENCRYPT_KEY is not configured in .env file');
    throw new Error('Encryption key not configured');
  }
  
  return key;
};

/**
 * Check if encryption is properly configured
 * @returns true if encryption key is available
 */
export const isEncryptionConfigured = (): boolean => {
  const key = getEncryptKey();
  return !!key;
};

/**
 * Encrypt data using TripleDES encryption
 * @param data - Data to encrypt (will be stringified)
 * @returns Encrypted string
 */
export const Encrypt = async (data: any): Promise<string> => {
  const SECRET_KEY = getEncryptionKey();
  return CryptoJS.TripleDES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

/**
 * Encrypt data using TripleDES and encode for URL
 * @param data - Data to encrypt (will be stringified)
 * @returns Encrypted and URL-encoded string
 */
export const EncryptEncode = async (data: any): Promise<string> => {
  const SECRET_KEY = getEncryptionKey();
  const encryptedData = CryptoJS.TripleDES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  return encodeURIComponent(encryptedData);
};

/**
 * Decrypt data using TripleDES decryption
 * @param encryptedResponse - Response object with encrypted data
 * @returns Decrypted data (parsed as JSON if possible)
 */
export const Decrypt = async (encryptedResponse: any): Promise<any> => {
  const SECRET_KEY = getEncryptionKey();
  const decryptedData = CryptoJS.TripleDES.decrypt(encryptedResponse, SECRET_KEY).toString(CryptoJS.enc.Utf8);
  let data = null;

  try {
    data = JSON.parse(decryptedData);
  } catch (err) {
    // Silently fail if not valid JSON
  }

  return data;
};

